import React from "react";

import * as Styled from "./styles";

import {
  Box,
  Button,
  CircularProgress,
  Container,
  Modal,
  TextField,
  Typography,
} from "@mui/material";
import { ReturnButton } from "components/ReturnButton";
import { Controller, useForm } from "react-hook-form";

import SignatureCanvas from "react-signature-canvas";

import { IMaskInput } from "react-imask";
import { useAuth } from "shared/providers/auth";
import toast from "react-hot-toast";
import { IConductTerm } from "shared/types/ConductTerm";
import { sendConductTerm } from "requests/ConductTerm";
import { PostSubmitModal } from "../PostSubmitModal";
import { pdfDownload } from "requests/PdfDownload";

interface IProps {
  open: boolean;
  close: (open: boolean) => void;
  pacId: number;
  onCompleteOrto?: () => void;
  emailPatient?: string;
}

const ConductTermReferrals: React.FC<IProps> = ({
  open,
  close,
  pacId,
  onCompleteOrto,
  emailPatient,
}) => {
  const { user } = useAuth();
  const {
    control,
    handleSubmit,
    formState: { errors },
    //reset,
  } = useForm<IConductTerm>();
  const [loading, setLoading] = React.useState<boolean>(false);

  const [signatureDoctor, setSignatureDoctor] = React.useState<any>();
  const [signaturePatient, setSignaturePatient] = React.useState<any>();
  const [showPostModal, setShowPostModal] = React.useState(false);
  const [idForm, setIdForm] = React.useState<number>(0);

  const sigCanvasPatient = React.useRef<SignatureCanvas>(null);
  const sigCanvasDentist = React.useRef<SignatureCanvas>(null);
  const sigBoxRef = React.useRef<HTMLDivElement>(null);
  const [canvasWidth, setCanvasWidth] = React.useState<number>(0);

  React.useEffect(() => {
    if (!open) return;

    const measure = () => {
      if (sigBoxRef.current) {
        setCanvasWidth(sigBoxRef.current.clientWidth - 2);
      }
    };

    const timer = setTimeout(measure, 50);
    window.addEventListener("resize", measure);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", measure);
    };
  }, [open]);

  const compressSignature = (canvas: HTMLCanvasElement, maxWidth = 400): string => {
    const scale = canvas.width > maxWidth ? maxWidth / canvas.width : 1;
    const resized = document.createElement("canvas");
    resized.width = canvas.width * scale;
    resized.height = canvas.height * scale;
    const ctx = resized.getContext("2d");
    if (ctx) {
      ctx.drawImage(canvas, 0, 0, resized.width, resized.height);
    }
    return resized.toDataURL("image/png");
  };

  const saveSignaturePatient = () => {
    if (sigCanvasPatient.current) {
      const trimmed = sigCanvasPatient.current.getTrimmedCanvas();
      setSignaturePatient(compressSignature(trimmed));
    }
  };

  const saveSignatureDentist = () => {
    if (sigCanvasDentist.current) {
      const trimmed = sigCanvasDentist.current.getTrimmedCanvas();
      setSignatureDoctor(compressSignature(trimmed));
    }
  };

  const clearSignaturePatient = () => {
    sigCanvasPatient.current?.clear();
    setSignaturePatient(null);
  };

  const clearSignatureDentits = () => {
    sigCanvasDentist.current?.clear();
    setSignatureDoctor(null);
  };

  const onSubmit = async (data: IConductTerm) => {
    if (!signaturePatient) {
      toast.error("Preencha a assinatura do paciente");
      return false;
    }
    if (!signatureDoctor) {
      toast.error("Preencha a assinatura do dentista");
      return false;
    }

    data.retorno_dias = Number(data.retorno_dias);
    data.retorno_semanas = Number(data.retorno_semanas);

    const fichaCompleta: IConductTerm = {
      ...data,
      assinatura_paciente: signaturePatient,
      assinatura_dentista: signatureDoctor,
      paciente_id: pacId,
    };

    try {
      setLoading(true);
      const { status, data: dataForm } = await sendConductTerm(
        fichaCompleta,
        user.data.id
      );

      if ([200, 201].includes(status)) {
        toast.success("Formulário enviado com sucesso");
        setIdForm(dataForm.data.NR_SEQ_CONTRATOFORM_CFOC as number);
        setLoading(false);
        setShowPostModal(true);
        onCompleteOrto?.();
      } else {
        setLoading(false);
        toast.error("Erro ao enviar formulário");
      }
    } catch (e) {
      console.log(e);
      setLoading(false);
    }
  };

  const onDownload = async () => {
    try {
      const { status, data } = await pdfDownload(
        { paciente_id: pacId, form_type: "termo_conduta" },
        user.data.id
      );

      if ([200, 201].includes(status)) {
        const base64 = data.data;

        //@ts-ignore
        if (window && window.ReactNativeWebView) {
          //call rn to download
          //@ts-ignore
          window.ReactNativeWebView.postMessage(
            JSON.stringify({
              action: "downloadBase64File",
              value: base64,
            })
          );
        } else {
          const byteCharacters = atob(base64);
          const byteNumbers = new Array(byteCharacters.length)
            .fill(0)
            .map((_, i) => byteCharacters.charCodeAt(i));
          const byteArray = new Uint8Array(byteNumbers);
          const blob = new Blob([byteArray], { type: "application/pdf" });

          const link = document.createElement("a");
          link.href = URL.createObjectURL(blob);
          link.download = "termo_conduta.pdf";
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }

        toast.success("PDF baixado com sucesso");
      } else {
        toast.error(data.message);
      }
    } catch (e) {
      console.error(e);
      toast.error("Erro ao baixar o PDF");
    }
  };

  /*const clear = () => {
    sigCanvasDentist.current?.clear();
    setSignatureDoctor(null);
    sigCanvasPatient.current?.clear();
    setSignaturePatient(null);
    reset();
  };*/

  return (
    <Modal open={open} onClose={() => close(false)}>
      <Box sx={{ backgroundColor: "white", height: "100%", width: "100%" }}>
        <Container maxWidth="xl" sx={{ overflow: "scroll", height: "100%" }}>
          <ReturnButton title="Termo de conduta" onClick={close} />
          <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
            <Controller
              name="responsavel"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Responsável (Caso necessário)"
                  fullWidth
                  margin="normal"
                  error={!!errors.responsavel}
                  helperText={errors.responsavel?.message}
                />
              )}
            />

            <Controller
              name="cpf"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  InputProps={{
                    inputComponent: IMaskInput as any,
                    inputProps: {
                      mask: "000.000.000-00",
                    },
                  }}
                  label="CPF do Responsável (Caso necessário)"
                  fullWidth
                  margin="normal"
                  error={!!errors.cpf}
                  helperText={errors.cpf?.message}
                />
              )}
            />

            <p style={{ marginTop: "10px" }}>
              Paciente declara estar ciente de como proceder em relação ao uso
              de:
            </p>
            <Controller
              name="uso"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Uso"
                  fullWidth
                  margin="normal"
                  multiline
                  rows={4}
                  error={!!errors.uso}
                  helperText={errors.uso?.message}
                />
              )}
            />

            <p style={{ marginTop: "10px" }}>
              Deve-se retornar em um período aproximado de:
            </p>

            <Styled.BoxLine>
              <Controller
                name="retorno_dias"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Retorno (Dias)"
                    fullWidth
                    type="number"
                    margin="normal"
                    error={!!errors.uso}
                    helperText={errors.uso?.message}
                  />
                )}
              />

              <p>ou</p>

              <Controller
                name="retorno_semanas"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Retorno (Semanas)"
                    fullWidth
                    type="number"
                    margin="normal"
                    error={!!errors.uso}
                    helperText={errors.uso?.message}
                  />
                )}
              />
            </Styled.BoxLine>
            <p>
              Para acompanhamento da aparatologia acima especificada, que foi
              instalada na data de hoje.
            </p>

            <Styled.BoxClinicalSignature className="boxValue">
              <Typography
                variant="h5"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flexDirection: "row",
                  justifyContent: "space-between",
                }}
              >
                Assinatura do Paciente ou Responsável
                <Button onClick={clearSignaturePatient} className="no-print">
                  Limpar
                </Button>
              </Typography>
              <div className="boxSignature" ref={sigBoxRef}>
                {canvasWidth > 0 && (
                  <SignatureCanvas
                    onEnd={saveSignaturePatient}
                    ref={sigCanvasPatient}
                    penColor="black"
                    canvasProps={{
                      height: 120,
                      className: "sigCanvas",
                      width: canvasWidth,
                    }}
                  />
                )}
              </div>
            </Styled.BoxClinicalSignature>

            <Styled.BoxClinicalSignature className="boxValue">
              <Typography
                variant="h5"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flexDirection: "row",
                  justifyContent: "space-between",
                }}
              >
                Assinatura do Dentista
                <Button onClick={clearSignatureDentits} className="no-print">
                  Limpar
                </Button>
              </Typography>
              <div className="boxSignature">
                {canvasWidth > 0 && (
                  <SignatureCanvas
                    onEnd={saveSignatureDentist}
                    ref={sigCanvasDentist}
                    penColor="black"
                    canvasProps={{
                      height: 120,
                      className: "sigCanvas",
                      width: canvasWidth,
                    }}
                  />
                )}
              </div>
            </Styled.BoxClinicalSignature>

            <Button
              type="submit"
              variant="contained"
              color="primary"
              sx={{ mt: 2, mb: 2 }}
            >
              {loading ? <CircularProgress color="inherit" /> : "Enviar"}
            </Button>
          </Box>
        </Container>

        <PostSubmitModal
          open={showPostModal}
          onClose={() => {
            setShowPostModal(false);
            //clear();
            close(false);
          }}
          onDownload={onDownload}
          onSendWhatsApp={() => {
            toast.success("Link enviado no WhatsApp");
          }}
          defaultEmail={emailPatient as string}
          formId={idForm}
          userId={user.data.id}
          userWpp={""}
        />
      </Box>
    </Modal>
  );
};

export { ConductTermReferrals };
